// eslint-disable-next-line import/no-cycle
import CustomerLocation from '@models/customer-location';
import Model from '@models/model';
import OrderItem from '@models/order-item';
// eslint-disable-next-line import/no-cycle
import RouteTask from '@models/route-task';
import Vue from '@vue/compat';

export const ORDER_STATUS_DRAFT = 'Draft';
export const ORDER_STATUS_FULFILLED = 'Fulfilled';
export const ORDER_STATUS_INVOICED = 'Invoiced';
export const ORDER_STATUS_NEW = 'New';
export const ORDER_STATUS_OPEN = 'Open';
export const ORDER_STATUS_VERIFIED = 'Verified';
export const ORDER_STATUS_SENT_TO_DMS = 'Sent to DMS';
export const ORDER_STATUS_VOIDED = 'Voided';
export const ORDER_STATUS_CLOSED = 'Closed';

export const ORDER_TYPE_DELIVERY = 'Delivery';
export const ORDER_TYPE_PICK_UP = 'Pick Up';
export const ORDER_TYPE_SERVICE = 'Service';
export const ORDER_TYPE_WILL_CALL = 'Will Call';
export const ORDER_TYPE_PROJECT = 'Project';
export const ORDER_TYPE_MEMBRANE_CLEAN = 'Membrane Cleaning';

export const ORDER_TYPES = [
    { id: ORDER_TYPE_DELIVERY, text: ORDER_TYPE_DELIVERY },
    { id: ORDER_TYPE_SERVICE, text: ORDER_TYPE_SERVICE },
    { id: ORDER_TYPE_WILL_CALL, text: ORDER_TYPE_WILL_CALL },
    { id: ORDER_TYPE_PICK_UP, text: ORDER_TYPE_PICK_UP },
    { id: ORDER_TYPE_PROJECT, text: ORDER_TYPE_PROJECT },
    { id: ORDER_TYPE_MEMBRANE_CLEAN, text: ORDER_TYPE_MEMBRANE_CLEAN },
];

export const ORDER_STATUSES = [
    { id: ORDER_STATUS_NEW, text: ORDER_STATUS_NEW },
    { id: ORDER_STATUS_OPEN, text: ORDER_STATUS_OPEN },
    { id: ORDER_STATUS_FULFILLED, text: ORDER_STATUS_FULFILLED },
    { id: ORDER_STATUS_VERIFIED, text: ORDER_STATUS_VERIFIED },
    { id: ORDER_STATUS_INVOICED, text: ORDER_STATUS_INVOICED },
    { id: ORDER_STATUS_CLOSED, text: ORDER_STATUS_CLOSED },
    { id: ORDER_STATUS_VOIDED, text: ORDER_STATUS_VOIDED },
];

export default class Order extends Model {
    static className = 'Order';

    index = true;

    sendRecentlyViewed = true;

    items = [];

    job = {};

    customer_purchase_order = {};

    vehicle = {};

    route_tasks = [];

    created_by = {};

    fulfilled_by = {};

    customer_location = { customer: {} };

    status = ORDER_STATUS_DRAFT;

    static allowsEdit = true;

    static formFieldsConfig = {
        status: {
            type: 'select',
            options: ORDER_STATUSES,
        },
    }

    static allowedFieldEdits = [
        'status',
    ];

    constructor(data) {
        super();
        this.setAttributes(data);
    }

    relations() {
        return {
            items: OrderItem,
            customer_location: CustomerLocation,
            route_tasks: RouteTask,
            route_task: RouteTask,
        };
    }

    resource() {
        return 'orders';
    }

    async open() {
        try {
            await this.post('open');
        } catch (error) {
            Vue.swal({
                title: 'Error',
                text: error.message,
                icon: 'error',
            });

            throw error;
        }
    }

    isDeliveryOrder() {
        return this.type === ORDER_TYPE_DELIVERY;
    }

    isPickUpOrder() {
        return this.type === ORDER_TYPE_PICK_UP;
    }

    isWillCallOrder() {
        return this.type === ORDER_TYPE_WILL_CALL;
    }

    isServiceOrder() {
        return this.type === ORDER_TYPE_SERVICE;
    }

    isMembraneCleanOrder() {
        return this.type === ORDER_TYPE_MEMBRANE_CLEAN;
    }

    isFulfilled() {
        return this.status === ORDER_STATUS_FULFILLED;
    }

    isVerified() {
        return this.status === ORDER_STATUS_VERIFIED;
    }

    isInReview() {
        return this.status === ORDER_STATUS_FULFILLED || this.status === ORDER_STATUS_VERIFIED;
    }

    isPreFulfilled() {
        return !this.status
            || this.status === ORDER_STATUS_DRAFT
            || this.status === ORDER_STATUS_NEW
            || this.status === ORDER_STATUS_OPEN
            || this.status === ORDER_STATUS_VOIDED;
    }

    isPreVerified() {
        return !this.status
            || this.status === ORDER_STATUS_DRAFT
            || this.status === ORDER_STATUS_NEW
            || this.status === ORDER_STATUS_OPEN
            || this.status === ORDER_STATUS_VOIDED
            || this.status === ORDER_STATUS_FULFILLED;
    }

    createOrderItem(item) {
        return new OrderItem({
            id: Math.random().toString(36).slice(2, 11),
            customer_location_item: item,
            customer_location_item_id: item.id,
            customer_location_service_contract_id: item.customer_location_service_contract_id,
            customer_location_service_contract: item.customer_location_service_contract,
            item_id: item.customer_location_service_contract_id ? item.item_id : null,
            item: item.customer_location_service_contract_id ? item.item : null,
            qty: item.qty,
            item_type_id: item.item_type_id,
            description: item.getDescription(),
            order_id: this.id,
            price: item.price,
            subtotal: item.price * item.qty,
            parent_order_item_id: null,
            gl_code_id: item.gl_code_id || item.item.gl_code_id,
            gl_code: item.gl_code_id ? item.gl_code : item.item.gl_code,
            note: item.note,
        });
    }

    static getPendingOrders(types, dateRange, branchId, date) {
        return this.whereIn('type', types)
            .where('filterable_status',
                types.includes(ORDER_TYPE_SERVICE) ? 'unscheduled' : 'pending')
            .when(branchId, (query) => {
                query.where('branch_id', branchId);
            })
            .when(dateRange, (query) => {
                query.where('date_range', dateRange);
            })
            .where('today_and_before', date)
            .where('has_non_ship_direct', true)
            .with('customerLocation.customer', 'branch', 'items', 'items.item')
            .orderBy('customer_location_name');
    }

    static getOrdersWithPastDueCustomers(type, dateRange, branchId, date) {
        return this.where('type', type)
            .where('past_due_customer', true)
            .whereIn('filterable_status', [ORDER_STATUS_NEW, ORDER_STATUS_OPEN])
            .when(branchId, (query) => {
                query.where('branch_id', branchId);
            })
            .when(dateRange, (query) => {
                query.where('date_range', dateRange);
            })
            .when(date, (query) => {
                query.where('date', date);
            })
            .with('customerLocation.customer', 'branch', 'customerLocation.customer.latestCustomerFinancialIndex', 'customerLocation.latestCustomerFinancialIndex')
            .orderBy('customer_name');
    }

    static getMigrateOrder() {
        return this
            .with('customerLocation.customer', 'branch')
            .orderBy('customer_location_name')
            .whereIn('status', ['Migrate']);
    }

    static getPendingPickUpOrders(branchId, date = null) {
        return Order
            .with('customerLocation.customer', 'branch', 'items')
            .where('today_and_before', date)
            .where('type', ORDER_TYPE_WILL_CALL)
            .whereIn('filterable_status', [ORDER_STATUS_NEW, ORDER_STATUS_OPEN])
            .when(branchId, (query) => {
                query.where('branch_id', branchId);
            });
    }

    static getShippingOrders(branchId) {
        return this.where('has_ship_from_puretec_items', true)
            .when(branchId, (query) => {
                query.where('branch_id', branchId);
            })
            .with('customerLocation.customer', 'items', 'createdBy')
            .orderBy('customer_location_name')
            .limit(250);
    }

    static getUnassignedOrders(type, dateRange, branchId) {
        return this.where('type', type)
            .where('filterable_status', 'unassigned')
            .when(branchId, (query) => {
                query.where('branch_id', branchId);
            })
            .when(dateRange, (query) => {
                query.where('date_range', dateRange);
            })
            .with('customerLocation.customer', 'items')
            .orderBy('customer_location_name')
            .limit(250)
            .get();
    }

    static getUnscheduledOrders(type, dateRange, branchId) {
        return this.where('type', type)
            .where('filterable_status', 'unscheduled')
            .when(branchId, (query) => {
                query.where('branch_id', branchId);
            })
            .when(dateRange, (query) => {
                query.where('date_range', dateRange);
            })
            .with('customerLocation.customer', 'items')
            .orderBy('customer_location_name')
            .limit(250);
    }

    async convertToInvoice(invoiceSeparately = false) {
        try {
            return this.post('invoice', { invoice_separately: invoiceSeparately });
        } catch (error) {
            Vue.swal({
                title: 'Error',
                text: error.message,
                icon: 'error',
            });

            throw error;
        }
    }

    static convertToInvoiceById(orderId, invoiceSeparately = false) {
        return new Order({ id: orderId }).convertToInvoice(invoiceSeparately);
    }

    hasCompletedRouteTasks() {
        return this.route_tasks.every((task) => task.isCompleted());
    }
}
